import ClientCache from "@/utils/login/ClientCache.ts";
import Translator from "@/utils/common/Translator.ts";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import NamaCommon, {NamaAxiosRequestConfig} from "@/utils/common/NamaCommon.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import LoginUtils from "@/utils/login/LoginUtils.ts";
import type {EditPresenter} from "@/utils/presenters/EditPresenter.ts";
import RouteNames from "@/router/RouteNames.ts";
import PlaceTokens from "@/utils/constants/PlaceTokens.ts";
import ViewMode from "@/utils/constants/ViewMode.ts";
import type {RouteLocationRaw} from "vue-router";
import {WidgetsPresenter} from "@/utils/presenters/interfaces.ts";
import {Block} from "@/utils/common/BlockUtils.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import EditScreenField = namaTypes.EditScreenField;

export type FieldSearcher = (searchValue: string) => FieldToFocusSearchItem[]
export type EditPresenterRetriever = () => WidgetsPresenter

export default class TopPanelSearchUtils {
  private static entities: SearchItem[] = [];
  private static fieldSearchers: FieldSearcher[] = [];
  private static editPresenterRetrievers: EditPresenterRetriever[] = [];

  public static getFieldSearchers() {
    return this.fieldSearchers;
  }

  public static findAnyEditPresenter(): WidgetsPresenter | undefined {
    if (this.editPresenterRetrievers.length)
      return this.editPresenterRetrievers[0]();
  }

  public static pushFieldSearcher(searcher: FieldSearcher) {
    this.fieldSearchers.push(searcher);
  }

  public static popFieldSearcher(searcher: FieldSearcher) {
    let index = this.fieldSearchers.indexOf(searcher);
    if (index >= 0)
      this.fieldSearchers.splice(index, 1);
  }

  public static pushEditPresenterRetriever(presenterRetriever: EditPresenterRetriever) {
    this.editPresenterRetrievers.push(presenterRetriever);
  }

  public static popEditPresenterRetriever(presenterRetriever: EditPresenterRetriever) {
    let index = this.editPresenterRetrievers.indexOf(presenterRetriever);
    if (index >= 0)
      this.editPresenterRetrievers.splice(index, 1);
  }

  public static searchForFields(searchValue: string): FieldToFocusSearchItem[] {
    if (!this.fieldSearchers.length)
      return [];
    for (let i = this.fieldSearchers.length - 1; i >= 0; i--) {
      let searcher = this.fieldSearchers[i];
      let result = searcher(searchValue);
      if (result && result.length)
        return result;
    }
    return [];
  }

  static async searchFor(value: string, searchInEntity?: string | null) {
    this.initEntitiesIfNeeded();
    value = value.toLowerCase();
    //TODO:: if (!getSelectedSearchIn()) {
    if (!searchInEntity) {
      let matchingEntities = this.entities.filter(e =>
        e.searchData && (e.searchData.includes(value)
          || e.searchData.includes(StringUtils.normalizeArabic(value))
          || e.searchData.includes(StringUtils.normalizeArabic(StringUtils.englishToArabic(value)))
          || e.searchData.includes(StringUtils.arabicToEnglish(value).toLowerCase()))
      );
      if (matchingEntities.length)
        return this.sortBySearchQuery(matchingEntities, value);
      if (ClientCache.mustSelectEntityInSearchInBeforeSearchOnServer())
        return [];
    } else {
      value = searchInEntity + ":" + value;
    }
    return await this.searchInServer(value);
  }

  static async searchInServer(value: string) {
    let settings: NamaAxiosRequestConfig = {
      params: {
        request: JSON.stringify({
          pageSize: 50,
          startPage: 0,
          query: value
        })
      },
      noLoadingIndicator: true,
    }
    let data = await NamaCommon.namaFetch(NamaURLS.SEARCH_ALL, settings);
    let convertedData: SearchItem[] = [];
    if (data.page)
      convertedData.push(...data.page.map((ref: namaTypes.EntityReferenceData) => {
        return {
          record: ref,
          type: "record",
          text: Translator.translateId(ref.entityType) + ": " + Translator.translateRef(ref)
        } as SearchItem
      }));
    return convertedData;
  }

  static searchEntitiesOnly(query: string): SearchItem[] {
    this.initEntitiesIfNeeded();
    const value = query.toLowerCase();
    if (!value) return this.entities.filter(e => e.type === 'entity').slice(0, 8);
    const matches = this.entities.filter(e => e.type === 'entity' && this.isValueInSearchItem(e, value));
    return this.sortBySearchQuery(matches, value);
  }

  static resolveSearchItemRoute(item: SearchItem): RouteLocationRaw | null {
    if (item.type === "entity") {
      return {
        name: RouteNames.LIST_VIEW_ROUTE,
        query: {[PlaceTokens.PRM_ENTITY_TYPE]: item.id}
      };
    } else if (item.type === "record" && item.record) {
      return {
        name: RouteNames.EDIT_VIEW_ROUTE,
        query: {
          [PlaceTokens.PRM_ENTITY_TYPE]: item.record.entityType,
          [PlaceTokens.PRM_ID]: item.record.id,
          [PlaceTokens.PRM_VIEW]: ViewMode.EDIT,
        }
      };
    } else if (item.type === "InternalLink") {
      return {name: item.id};
    }
    return null;
  }

  private static sortBySearchQuery(matchingEntities: SearchItem[], value: string) {
    matchingEntities.sort((a, b) => a.id === b.id ? 0 : a.searchData!.length - b.searchData!.length)
    return matchingEntities;
  }
  private static initEntitiesIfNeeded() {
    if (this.entities.length)
      return;
    for (let entity of ClientCache.getAllEntityTypes()) {
      let ar = Translator.translateIdAr(entity)!;
      let en = Translator.translateIdEn(entity)!;
      let item: SearchItem = {
        id: entity,
        ar: ar,
        en: en,
        type: "entity",
        text: Translator.translateId(entity)!,
        searchData: entity.toLowerCase() + " " + ar.toLowerCase() + " " + StringUtils.normalizeArabic(ar.toLowerCase()) + " " +
          Translator.translateIdAr(entity + "s")!.toLowerCase() + " " + StringUtils.normalizeArabic(Translator.translateIdAr(entity + "s")!.toLowerCase()) + " " +
          en.toLowerCase() + " " + Translator.translateIdEn(entity + "s")!.toLowerCase()
      };
      this.entities.push(item)
    }
    this.appendUtilsToEntitiesIfNeeded();
  }

  private static appendUtilsToEntitiesIfNeeded() {
    if (LoginUtils.loginInfo?.userInfo?.loginId !== 'admin' && !LoginUtils.loginInfo?.userInfo?.settings?.treatAsAdmin)
      return;
    const UTILS_SEARCH_DATA = "utils عفهمس utilities عفهمهفهثس test فثسف ";
    this.entities.push({
      id: RouteNames.UTILS_ROUTE,
      ar: "Utilities",
      en: "Utilities",
      type: 'InternalLink',
      text: "Utilities",
      searchData: UTILS_SEARCH_DATA,
    })
    this.entities.push({
      id: RouteNames.VIEW_USERS_ROUTE,
      ar: "View Users",
      en: "View Users",
      type: 'InternalLink',
      text: "View Users",
      searchData: UTILS_SEARCH_DATA + "view users هثص عسثقسر",
    })
    this.entities.push({
      id: RouteNames.MONITOR_TASKS_ROUTE,
      ar: "Monitor Tasks",
      en: "Monitor Tasks",
      type: 'InternalLink',
      text: "Monitor Tasks",
      searchData: UTILS_SEARCH_DATA + "monitor tasks ةخىهفخق فشسنس",
    })
    this.entities.push({
      id: RouteNames.SQL_RUNNER_ROUTE,
      ar: "SQL Runner",
      en: "SQL Runner",
      type: 'InternalLink',
      text: "SQL Runner",
      searchData: UTILS_SEARCH_DATA + "sql query runner سضم ضعثقغ قعىىثق",
    })
    this.entities.push({
      id: RouteNames.WEBSOCKET_SESSIONS,
      ar: "View Web Socket Sessions",
      en: "View Web Socket Sessions",
      type: 'InternalLink',
      text: "View Web Socket Sessions",
      searchData: UTILS_SEARCH_DATA + "view web socket sessions رهثص صثلا سخؤنثف سثسسهخىس",
    })
  }

  static fetchEntitiesForSearchIn(): SearchItem[] {
    if (!this.entities.length)
      this.initEntitiesIfNeeded();
    return this.entities;
  }

  static getUniqueFieldIds(): (string | undefined)[] {
    return this.searchForAllUniqueFields().map(f => f.id)
  }

  static getAllFieldIds(): (string | undefined)[] {
    return this.searchForAllFields().map(f => f.id)
  }

  static searchForAllFields(): FieldToFocusSearchItem[] {
    return this.searchForFields('');
  }

  static searchForAllUniqueFields() {
    return this.searchForAllFields().filter((field, index, array) => array.findIndex(f => f.id == field.id) == index)
  }

  static isValueInSearchItem(searchItem?: SearchItem, value?: string): searchItem is SearchItem {
    if (ObjectChecker.isNullOrUndefined(value))
      value = '';
    if (searchItem?.searchData)
      return (searchItem.searchData.includes(value)
        || searchItem.searchData.includes(StringUtils.normalizeArabic(value))
        || searchItem.searchData.includes(StringUtils.normalizeArabic(StringUtils.englishToArabic(value)))
        || searchItem.searchData.includes(StringUtils.arabicToEnglish(value).toLowerCase()))

    return false;
  }
}

export interface SearchItem {
  presenterId?: number;
  id?: string;
  ar?: string;
  en?: string;
  type: string;
  text: string;
  searchData?: string;
  record?: namaTypes.EntityReferenceData;
  name1?: string;
  name2?: string;
  code?: string;
  actualCode?: string;
}

export interface FieldToFocusSearchItem extends SearchItem {
  record: namaTypes.EntityReferenceData & {
    extraData: {
      block: Block,
      blockId: string,
      field: EditScreenField,
      presenter: EditPresenter,
      pageTitle: string,
      agRowIndex?: number
    }
  }
}

export interface FieldToFocusOptions {
  field?: FieldToFocusSearchItem,
  dontFocus?: boolean,
  dontFlash?: boolean
}

